/**
 * F-tarefas (CEO 2026-10-29) — "Minhas tarefas": atrasadas, de hoje e dos
 * próximos 7 dias, com "Feito" (comentário opcional) e, na recorrente,
 * "Não se aplica" (motivo obrigatório). Usado na página Tarefas (ERP) e no
 * portal do chantier (`variant="portal"`, fundo escuro).
 */
import { useState } from "react";
import { useI18n, useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";

type Variant = "erp" | "portal";

const INTL: Record<string, string> = {
  fr: "fr-FR",
  pt: "pt-BR",
  en: "en-GB",
  es: "es-ES",
  uk: "uk-UA",
};

/** Formata o prazo no idioma da pessoa ("qui., 30/10 18:00"). */
export function useFmtPrazo(): (iso: string) => string {
  const { locale } = useI18n();
  const intl = INTL[locale] ?? "fr-FR";
  return (iso) => fmtPrazo(iso, intl);
}

export function fmtPrazo(iso: string, intl = "fr-FR"): string {
  const d = new Date(iso);
  const dia = d.toLocaleDateString(intl, { weekday: "short", day: "2-digit", month: "2-digit" });
  const hora = d.toLocaleTimeString(intl, { hour: "2-digit", minute: "2-digit" });
  return `${dia} ${hora}`;
}

export function MyTasksList({
  variant = "erp",
  showEmpty = true,
}: {
  variant?: Variant;
  showEmpty?: boolean;
}) {
  const t = useT();
  const fmt = useFmtPrazo();
  const utils = trpc.useUtils();
  const mineQ = trpc.tasks.mine.useQuery(undefined, { refetchInterval: 5 * 60_000 });
  const refresh = () => {
    void utils.tasks.invalidate();
  };
  const doneMut = trpc.tasks.complete.useMutation({ onSuccess: refresh });
  const skipMut = trpc.tasks.skip.useMutation({ onSuccess: refresh });
  const [aberta, setAberta] = useState<{ id: number; modo: "done" | "skip" } | null>(null);
  const [texto, setTexto] = useState("");

  const rows = mineQ.data?.rows ?? [];
  const multiEmpresa = new Set(rows.map((r) => r.companyId)).size > 1;
  const dark = variant === "portal";
  const grupos = [
    {
      key: "overdue",
      label: t("tasks.groupOverdue"),
      rows: rows.filter((r) => r.bucket === "overdue"),
    },
    { key: "today", label: t("tasks.groupToday"), rows: rows.filter((r) => r.bucket === "today") },
    {
      key: "upcoming",
      label: t("tasks.groupUpcoming"),
      rows: rows.filter((r) => r.bucket === "upcoming"),
    },
  ] as const;

  if (mineQ.isLoading) return null;
  if (rows.length === 0) {
    return showEmpty ? (
      <p
        className={dark ? "text-sm text-lime" : "text-sm"}
        style={dark ? undefined : { color: "var(--text-muted)" }}
        data-testid="mytasks-empty"
      >
        ✓ {t("tasks.mineEmpty")}
      </p>
    ) : null;
  }

  const erro = doneMut.error?.message ?? skipMut.error?.message;
  const cardStyle = (overdue: boolean): React.CSSProperties | undefined =>
    dark
      ? undefined
      : {
          background: "var(--surface-card)",
          border: "1px solid var(--border)",
          borderLeft: `4px solid ${overdue ? "var(--danger-500)" : "var(--info-500)"}`,
        };

  return (
    <div className="space-y-4" data-testid="mytasks">
      {erro && <p className="text-xs text-red-500">{erro}</p>}
      {grupos
        .filter((g) => g.rows.length > 0)
        .map((g) => (
          <section key={g.key} className="space-y-2" data-testid={`mytasks-group-${g.key}`}>
            <h3
              className={`text-xs font-bold uppercase tracking-wider ${
                g.key === "overdue" ? "text-red-500" : dark ? "text-neutral-400" : ""
              }`}
              style={g.key === "overdue" || dark ? undefined : { color: "var(--text-muted)" }}
            >
              {g.label} · {g.rows.length}
            </h3>
            {g.rows.map((r) => {
              const id = r.occurrenceId;
              const emEdicao = id != null && aberta?.id === id;
              return (
                <article
                  key={`${r.taskId}-${r.dueDate}`}
                  className={`space-y-1.5 rounded-lg px-3 py-2.5 ${
                    dark
                      ? `border ${g.key === "overdue" ? "border-red-500/60 bg-red-500/10" : "border-neutral-700 bg-neutral-900"}`
                      : ""
                  }`}
                  style={cardStyle(g.key === "overdue")}
                  data-testid="mytask-row"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p
                        className={`text-sm font-semibold ${dark ? "text-neutral-100" : ""}`}
                        style={dark ? undefined : { color: "var(--text-strong)" }}
                      >
                        {r.priority === "urgent" && (
                          <span
                            className={
                              dark
                                ? "mr-1.5 rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                                : "badge b-dang mr-1.5"
                            }
                            data-testid="mytask-urgent"
                          >
                            {t("tasks.urgent")}
                          </span>
                        )}
                        {r.title}
                      </p>
                      <p
                        className={`text-xs ${dark ? "text-neutral-400" : ""}`}
                        style={dark ? undefined : { color: "var(--text-muted)" }}
                      >
                        {fmt(r.dueAt)}
                        {r.recurring && ` · ↻ ${t("tasks.recurringBadge")}`}
                        {multiEmpresa && ` · ${r.companyName}`}
                      </p>
                    </div>
                    {id != null && !emEdicao && (
                      <div className="flex shrink-0 flex-wrap gap-1.5">
                        <button
                          type="button"
                          disabled={doneMut.isPending}
                          onClick={() => doneMut.mutate({ occurrenceId: id })}
                          className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                          data-testid="mytask-done"
                        >
                          ✔ {t("tasks.doneBtn")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAberta({ id, modo: "done" });
                            setTexto("");
                          }}
                          className={`rounded-md border px-2 py-1 text-xs ${dark ? "border-neutral-600 text-neutral-200" : "border-neutral-300"}`}
                          title={t("tasks.commentPh")}
                          aria-label={t("tasks.commentPh")}
                          data-testid="mytask-comment"
                        >
                          💬
                        </button>
                        {r.recurring && (
                          <button
                            type="button"
                            onClick={() => {
                              setAberta({ id, modo: "skip" });
                              setTexto("");
                            }}
                            className={`rounded-md border px-2 py-1 text-xs ${dark ? "border-neutral-600 text-neutral-200" : "border-neutral-300"}`}
                            data-testid="mytask-skip"
                          >
                            {t("tasks.skipBtn")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {r.description && (
                    <p
                      className={`whitespace-pre-line text-xs ${dark ? "text-neutral-300" : ""}`}
                      style={dark ? undefined : { color: "var(--text-body)" }}
                    >
                      {r.description}
                    </p>
                  )}
                  {emEdicao && aberta && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <input
                        autoFocus
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        maxLength={aberta.modo === "skip" ? 500 : 1000}
                        placeholder={
                          aberta.modo === "skip" ? t("tasks.skipReasonPh") : t("tasks.commentPh")
                        }
                        className={`min-w-0 flex-1 rounded-md border px-2 py-1 text-sm ${
                          dark ? "border-neutral-600 bg-neutral-950 text-neutral-100" : ""
                        }`}
                        data-testid="mytask-text"
                      />
                      <button
                        type="button"
                        disabled={
                          (aberta.modo === "skip" && texto.trim().length < 3) ||
                          doneMut.isPending ||
                          skipMut.isPending
                        }
                        onClick={() => {
                          const fim = () => setAberta(null);
                          if (aberta.modo === "skip") {
                            skipMut.mutate(
                              { occurrenceId: aberta.id, reason: texto.trim() },
                              { onSuccess: fim },
                            );
                          } else {
                            doneMut.mutate(
                              { occurrenceId: aberta.id, comment: texto.trim() || undefined },
                              { onSuccess: fim },
                            );
                          }
                        }}
                        className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                        data-testid="mytask-confirm"
                      >
                        {aberta.modo === "skip" ? t("tasks.confirmBtn") : `✔ ${t("tasks.doneBtn")}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAberta(null)}
                        className={`text-xs underline ${dark ? "text-neutral-400" : ""}`}
                      >
                        {t("tasks.backBtn")}
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        ))}
    </div>
  );
}
